import type { ChangeEvent, FocusEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { TextControl } from '@elfsight/widget-ui';
import { createReadableClass, validateTime } from '@elfsight/widget-utils';
import { parse } from 'fast-time-input/dist';
import type { CommonFieldProps } from '../types';

type TimeFormat = '12-hour' | '24-hour' | 'auto';

export type TimeComponentProps = CommonFieldProps<
  string,
  HTMLInputElement | HTMLTextAreaElement
> & {
  timeFormat: TimeFormat;
};

export function Time({
  value,
  timeFormat,
  onChange,
  label,
  language,
  showLabel = true,
  fieldFontStyle,
  fieldLabelPosition,
  fieldLabelColor,
  fieldLabelFontStyle,
  fieldColor,
  fieldFillColor,
  fieldBorderType,
  fieldBorderColor,
  fieldBorderWidth,
  fieldBorderRadius,
  ...forwardingProps
}: TimeComponentProps) {
  const [localValue, setLocalValue] = useState('');
  const [validValue, setValidValue] = useState('');
  const locale = language?.language ?? 'en';

  const determineTimeFormat = useCallback(
    (format: TimeFormat) => {
      if (format === '12-hour') {
        return '12hr';
      }

      if (format === '24-hour') {
        return '24hr';
      }

      const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric'
      };
      const parts = new Intl.DateTimeFormat(locale, options).formatToParts(
        new Date()
      );

      return parts.some((part) => part.type === 'dayPeriod') ? '12hr' : '24hr';
    },
    [locale]
  );

  const parseAndFormatTime = useCallback(
    (inputValue: string, format: TimeFormat) => {
      const parsedTime = parse(inputValue, determineTimeFormat(format));

      const isInvalidTime =
        (parsedTime === '00:00' || parsedTime === '12:00 AM') &&
        !inputValue.includes('0');

      const finalValue = isInvalidTime ? inputValue : parsedTime;

      return validateTime(finalValue) || !finalValue ? finalValue : validValue;
    },
    [determineTimeFormat, validValue]
  );

  useEffect(() => {
    setLocalValue((prevValue) =>
      prevValue ? parseAndFormatTime(prevValue, timeFormat) : prevValue
    );
  }, [parseAndFormatTime, timeFormat]);

  useEffect(() => {
    if (value) {
      const formattedValue = parseAndFormatTime(value, timeFormat);
      setLocalValue(formattedValue);
      setValidValue(formattedValue);

      const fakeEvent = {
        target: { value: formattedValue }
      } as ChangeEvent<HTMLInputElement>;

      onChange?.(formattedValue, fakeEvent);
    }
  }, [value, parseAndFormatTime, timeFormat]);

  const handleInputChange = (newInputValue: string) => {
    setLocalValue(newInputValue);
  };

  const handleInputBlur = (
    event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const formattedValue = parseAndFormatTime(localValue, timeFormat);
    setLocalValue(formattedValue);
    setValidValue(formattedValue);
    onChange?.(formattedValue, event);
  };

  return (
    <TextControl
      {...forwardingProps}
      className={createReadableClass({
        component: 'fields',
        element: 'time'
      })}
      type="text"
      label={showLabel && fieldLabelPosition === 'inside' ? label : undefined}
      labelFont={fieldLabelFontStyle}
      labelColor={fieldLabelColor}
      color={fieldColor}
      background={fieldFillColor}
      font={fieldFontStyle}
      borderType={fieldBorderType ? fieldBorderType : undefined}
      borderColor={fieldBorderColor}
      borderWidth={fieldBorderWidth}
      borderRadius={fieldBorderRadius}
      value={localValue}
      onValueChange={handleInputChange}
      onBlur={handleInputBlur}
    />
  );
}
